import React, { useContext } from "react";
import { Grid, IconButton } from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import RemoveIcon from "@mui/icons-material/RemoveCircleOutline";
import assert from "assert";
import { gridSpacing, rowMargin, TicketProps } from "../../FlightPackage/Ticket/FlightLegs/ticketFlightLegs";
import { TicketLocationExtended } from "../../Utils/TypeExtensions";
import { GlobalContext } from "../../Utils/Contexts";
import StringEditor from "../inputs/stringEditor";
import LocationEditor from "../inputs/locationEditor";
import NumberEditor from "../inputs/numberEditor";
import { saveTicketChanges } from "../../Utils/TicketDatabase";
import { FIELD_LIMITS } from "../../sharedCode/fieldLimits";

interface Props extends TicketProps {
    id: number;
    handleDelete: (legToDelete: number) => void;
    mergeRowChanges: (locationId: number, changes: Record<string, unknown>) => void;
    sortedLocations: TicketLocationExtended[];
}

export default function DurationRow(props: Props): JSX.Element {
    const context = useContext(GlobalContext);
    // TODO is this usage of assert appropriate? read up on it more
    assert(props.ticket.Locations);
    const flightLeg = props.ticket.Locations.find((l) => l?.id === props.id);
    assert(flightLeg);

    const sortedIndex = props.sortedLocations.findIndex((l) => l.id === props.id);
    const isFirstRow = sortedIndex <= 0;
    const isLastRow = sortedIndex < 0 || sortedIndex >= props.sortedLocations.length - 1;

    return (
        <Grid key={flightLeg.id + "r"} container flexDirection={"row"} justifyContent={"center"} alignItems={"center"}>
            <Grid key={`icons${props.id}`} item container justifyContent={"center"} alignItems={"center"} xs={1}>
                <Grid item>
                    <IconButton
                        disabled={props.isReadOnly || isFirstRow}
                        size="large"
                        edge="end"
                        onClick={() => {
                            handleItemMoveUp(flightLeg.id);
                        }}>
                        <KeyboardArrowUpIcon />
                    </IconButton>
                </Grid>
                <Grid item>
                    <IconButton
                        disabled={props.isReadOnly || isLastRow}
                        size="large"
                        edge="end"
                        onClick={() => {
                            handleItemMoveDown(flightLeg.id);
                        }}>
                        <KeyboardArrowDownIcon />
                    </IconButton>
                </Grid>
            </Grid>
            <Grid key={flightLeg.id + "r1"} item container xs={10} columnSpacing={gridSpacing} marginTop={rowMargin}>
                <Grid key={`a${props.id}`} item xs={12} md={7}>
                    <LocationEditor
                        sourceId={flightLeg.id}
                        sourceProperty="location"
                        labelText="LOCATION/LSD - NTS OR DLS"
                        isRequired={true}
                        initialValue={flightLeg.location}
                        mergeChanges={props.mergeRowChanges}
                        isDisabled={props.isReadOnly}
                        errors={props.errors}
                        containerUrl={"ticket/legs"}
                        company_id={props.ticket.company_id}
                        ticketList={props.ticketList}
                    />
                </Grid>
                <Grid key={`b${props.id}`} item xs={8} md={3}>
                    <StringEditor
                        key={`cc${flightLeg.location}${flightLeg.id}`}
                        sourceId={flightLeg.id}
                        sourceProperty="cost_code"
                        labelText="COST CODE/AFE"
                        isRequired={true}
                        initialValue={flightLeg.cost_code}
                        mergeChanges={props.mergeRowChanges}
                        isDisabled={props.isReadOnly}
                        errors={props.errors}
                        containerUrl={"ticket/legs"}
                        maxLength={FIELD_LIMITS.cost_code}
                    />
                </Grid>
                <Grid key={`duration${props.id}-${flightLeg.hours}`} item xs={4} md={2}>
                    <NumberEditor
                        sourceId={flightLeg.id}
                        sourceProperty="hours"
                        labelText="HOURS"
                        isRequired={true}
                        initialValue={flightLeg.hours}
                        mergeChanges={props.mergeRowChanges}
                        isDisabled={props.isReadOnly}
                        errors={props.errors}
                        containerUrl={"ticket/legs"}
                        precision={1}
                    />
                </Grid>
            </Grid>
            <Grid key={`f${props.id}`} item container justifyContent={"center"} xs={1}>
                <Grid item>
                    <IconButton
                        disabled={props.isReadOnly}
                        size="large"
                        edge="end"
                        onClick={() => {
                            props.handleDelete(flightLeg?.id);
                        }}>
                        <RemoveIcon />
                    </IconButton>
                </Grid>
            </Grid>
        </Grid>
    );

    function handleItemMoveUp(idToMoveUp: number): void {
        const locationToMoveUp = props.sortedLocations.find((p: TicketLocationExtended) => p.id === idToMoveUp);
        if (!locationToMoveUp) return;
        const indexToMoveUp = props.sortedLocations.indexOf(locationToMoveUp);
        if (indexToMoveUp <= 0) return;
        const newList = props.sortedLocations.slice(0);
        newList[indexToMoveUp].sort_order = indexToMoveUp - 1; // index = 3, order = 3 => 2
        newList[indexToMoveUp - 1].sort_order = indexToMoveUp; // index = 2, order = 2 => 3

        saveTicketChanges(props.ticket.id, {
            Locations: newList,
        }).catch(() => props.showForceReloadDialogue());
    }

    function handleItemMoveDown(idToMoveDown: number): void {
        const locationToMoveDown = props.sortedLocations.find((p: TicketLocationExtended) => p.id === idToMoveDown);
        if (!locationToMoveDown) return;
        const indexToMoveDown = props.sortedLocations.indexOf(locationToMoveDown);
        const nextIndex = indexToMoveDown + 1;
        if (nextIndex >= props.sortedLocations.length) return;
        const newList = props.sortedLocations.slice(0);
        newList[indexToMoveDown].sort_order = nextIndex; // index = 2, order = 2 => 3
        newList[nextIndex].sort_order = indexToMoveDown; // index = 3, order = 3 => 2

        saveTicketChanges(props.ticket.id, {
            Locations: newList,
        }).catch(() => props.showForceReloadDialogue());
    }
}
